import type { DocumentNode } from 'graphql';
import type { PropsWithChildren, ReactElement } from 'react';
import React from 'react';

interface LivePreviewProviderProps {
    locale: string;
    space?: string;
    environment?: string;
    debugMode?: boolean;
    enableInspectorMode?: boolean;
    enableLiveUpdates?: boolean;
    targetOrigin?: string | string[];
    experimental?: Record<string, unknown>;
}

interface LivePreviewUpdateOptions {
    locale?: string;
    query?: DocumentNode;
    skip?: boolean;
}

interface InspectorSharedProps {
    entryId?: string;
    assetId?: string;
    locale?: string;
    space?: string;
    environment?: string;
}

interface InspectorModeProps extends InspectorSharedProps {
    fieldId: string;
}

interface UseContentfulLiveUpdates {
    <T>(data: T, options?: LivePreviewUpdateOptions): T;
    <T>(data: T, locale?: string, skip?: boolean): T;
}

interface LivePreviewReactModule {
    ContentfulLivePreviewProvider: (
        props: PropsWithChildren<LivePreviewProviderProps>
    ) => ReactElement;
    useContentfulInspectorMode: (
        sharedProps?: InspectorSharedProps
    ) => (props: InspectorModeProps) => Record<string, string> | null;
    useContentfulLiveUpdates: UseContentfulLiveUpdates;
}

// CJS bridge for @contentful/live-preview/react. The package has a require export,
// but TypeScript's Node16 module checks reject a static import from this CJS file.
const livePreviewReact =
    // eslint-disable-next-line import/no-commonjs
    require('@contentful/live-preview/react') as LivePreviewReactModule;

const canUseContentfulLivePreview = () =>
    typeof window !== 'undefined' &&
    typeof window.addEventListener === 'function' &&
    typeof window.removeEventListener === 'function';

const contentfulLivePreviewAvailable = canUseContentfulLivePreview();

const noopContentfulInspectorMode = () => null;
const useNoopContentfulInspectorMode = () => noopContentfulInspectorMode;
const useNoopContentfulLiveUpdates: UseContentfulLiveUpdates = (data) => data;

export const ContentfulLivePreviewProvider = ({
    children,
    ...props
}: PropsWithChildren<LivePreviewProviderProps>): ReactElement => {
    if (!contentfulLivePreviewAvailable) {
        return React.createElement(React.Fragment, null, children);
    }

    return React.createElement(
        livePreviewReact.ContentfulLivePreviewProvider,
        props,
        children
    );
};
export const useContentfulInspectorMode: LivePreviewReactModule['useContentfulInspectorMode'] =
    contentfulLivePreviewAvailable
        ? livePreviewReact.useContentfulInspectorMode
        : useNoopContentfulInspectorMode;
export const useContentfulLiveUpdates: UseContentfulLiveUpdates =
    contentfulLivePreviewAvailable
        ? livePreviewReact.useContentfulLiveUpdates
        : useNoopContentfulLiveUpdates;
